import { NewCustomer } from "components/workOrder/types/customer";
import { get, post, put } from "../../../api/axios-client";
import { Customer } from "../models/workOrderTypes";

const controllerUrl = "api/Customer/";

// Customer details for editing
export interface CustomerDetails {
  customerId: number;
  customerNumber: string;
  customerName: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  billingAddress1?: string;
  billingAddress2?: string;
  city?: string;
  county?: string;
  state?: string;
  zip?: string;
  country?: string;
  billingZoneId?: number;
  phoneNumber?: string;
  email?: string;
  creditHold: boolean;
  billingAddressId?: number;
  primaryContactId?: number;
}

// Customer update request
export interface CustomerUpdateRequest {
  customerId: number;
  locationId: number;
  customerName?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  billingAddress1?: string;
  billingAddress2?: string;
  city?: string;
  county?: string;
  state?: string;
  zip?: string;
  country?: string;
  billingZoneId?: number;
  phoneNumber?: string;
  email?: string;
  modifiedBy: string;
}

export const CustomerAPI = {
  searchCustomersByName: async (
    customerName: string,
  ): Promise<Customer[] | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetCustomersByCustomerName?searchTerm=${customerName}`,
      );
      return data.content;
    } catch (err) {
      //console.log(err);
      return null;
    }
  },
  getCustomerImage: async (customerIdentity: number): Promise<Blob> => {
    try {
      const { data } = await get(`${controllerUrl}GetCustomerProfileImage`, {
        params: {
          customerIdentity,
        },
        responseType: "blob",
      });
      return data;
    } catch (error: any) {
      // Re-throw 404 errors so cache service can handle them
      if (error?.response?.status === 404) {
        throw error;
      }
      // For other errors, throw to be handled by caller
      throw error;
    }
  },
  getCustomerContactDetailsById: async (
    customerIdentity: number,
  ): Promise<Customer | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetCustomerContactByCustomerIdentity?customerIdentity=${customerIdentity}`,
      );
      return data.content;
    } catch (err) {
      //console.log(err);
      return null;
    }
  },
  isCustomerExist: async (customerNumber?: string): Promise<boolean> => {
    try {
      const response = await get(
        `${controllerUrl}IsCustomerExist?customerNumber=${customerNumber}`,
      );
      return response.data.content === true;
    } catch (error: any) {
      //console.log(err);
      throw error;
    }
  },

  createNewCustomer: async (customer: NewCustomer): Promise<number | null> => {
    try {
      const response = await post(`${controllerUrl}CreateCustomer`, customer);
      // Handle both wrapped (ServiceResponse) and unwrapped responses
      const result = response.data?.content ?? response.data;
      // Ensure we return a valid number or null
      if (typeof result === 'number' && result > 0) {
        return result;
      }
      return null;
    } catch (error: unknown) {
      throw error;
    }
  },

  /**
   * Get full customer details for editing
   */
  getCustomerDetails: async (customerId: number): Promise<CustomerDetails | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetCustomerDetails?customerId=${customerId}`,
      );
      // Handle different response structures
      const result = data.content || data.Content || data;
      return result;
    } catch (error: unknown) {
      console.error("Error fetching customer details:", error);
      return null;
    }
  },

  /**
   * Update an existing customer
   */
  updateCustomer: async (customer: CustomerUpdateRequest): Promise<boolean> => {
    try {
      const response = await put(`${controllerUrl}UpdateCustomer`, customer);
      return response.data.content === true;
    } catch (error: unknown) {
      console.error("Error updating customer:", error);
      throw error;
    }
  },
};
